"use client";

import { createClient } from "../supabase/client";
import type { AuthorizedFocusSourceRelation } from "../personalFocusEvidence";
import { PersonalFocusDataError } from "./personalFocus";

export const PERSONAL_FOCUS_SOURCE_PAGE_SIZE = 50;
export const PERSONAL_FOCUS_SOURCE_MAX_ROWS = 50;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PROPOSAL_COLUMNS = "workspace_id,batch_id,created_issue_id";
const BATCH_COLUMNS = "workspace_id,id,source_type,source_id";
type Row = Record<string, unknown>;
type Page = { data: unknown; error: unknown; count: number | null };
// These existing tables predate the generated Database type. Keep the cast
// narrowly read-only and validate every projected field before returning it.
interface Query extends PromiseLike<Page> {
  select(columns: string, options: { count: "exact" }): Query;
  eq(column: string, value: string): Query;
  in(column: string, values: string[]): Query;
  order(column: string, options: { ascending: true }): Query;
  range(from: number, to: number): Query;
  abortSignal(signal: AbortSignal): Query;
}
type SourceDb = { from(table: "task_proposals" | "task_proposal_batches"): Query };

function fail(code: ConstructorParameters<typeof PersonalFocusDataError>[0]): never {
  throw new PersonalFocusDataError(code);
}
function normalizeId(value: string): string {
  if (!UUID.test(value)) fail("invalid_input");
  return value.toLowerCase();
}
function isId(value: unknown): value is string {
  return typeof value === "string" && UUID.test(value);
}

/** A short server page is not EOF. Exact count or a final empty page proves
 * completion; over 50 matching rows fails without publishing a partial list.
 * This bounds even a misbehaving/cycling provider to 51 page requests per stage.
 */
async function completePages(makeQuery: (offset: number) => Query, check: () => void): Promise<Row[]> {
  const rows: Row[] = [];
  for (let pageIndex = 0; pageIndex <= PERSONAL_FOCUS_SOURCE_MAX_ROWS; pageIndex++) {
    check();
    const { data, error, count } = await makeQuery(rows.length);
    check();
    if (error) fail("query_failed");
    if (!Array.isArray(data) || data.some((row) => row === null || typeof row !== "object" || Array.isArray(row))) fail("invalid_response");
    if (count !== null && (!Number.isSafeInteger(count) || count < 0)) fail("invalid_response");
    if ((count !== null && count > PERSONAL_FOCUS_SOURCE_MAX_ROWS) || rows.length + data.length > PERSONAL_FOCUS_SOURCE_MAX_ROWS) fail("query_failed");
    rows.push(...data as Row[]);
    if (count !== null && rows.length > count) fail("invalid_response");
    if (count !== null && rows.length === count) return rows;
    if (data.length === 0) {
      if (count !== null && rows.length < count) fail("query_failed");
      return rows;
    }
  }
  fail("query_failed");
}

/** Read only applied proposal -> source relations under the viewer's browser
 * RLS. No source body, excerpt, title, private URL or external fetch is involved.
 * Returned doc IDs still require the UI's authorized document inventory before
 * they become links. Abort/session switches discard the complete result.
 */
export async function loadPersonalFocusSourceRelations(
  rawWorkspaceId: string, rawUserId: string, rawIssueId: string, signal?: AbortSignal,
): Promise<AuthorizedFocusSourceRelation[]> {
  const workspaceId = normalizeId(rawWorkspaceId);
  const userId = normalizeId(rawUserId);
  const issueId = normalizeId(rawIssueId);
  if (signal?.aborted) fail("aborted");
  let client: ReturnType<typeof createClient>;
  try { client = createClient(); } catch { fail("query_failed"); }
  const db = client as unknown as SourceDb;
  let identityChanged = false;
  let subscription: { unsubscribe: () => void } | undefined;
  const check = () => {
    if (signal?.aborted) fail("aborted");
    if (identityChanged) fail("session_changed");
  };
  const verifyIdentity = async () => {
    check();
    const { data, error } = await client.auth.getUser();
    check();
    if (error || data.user?.id !== userId) fail("unauthorized");
  };
  try {
    subscription = client.auth.onAuthStateChange((_event, session) => {
      if (session?.user?.id !== userId) identityChanged = true;
    }).data.subscription;
    await verifyIdentity();
    const proposals = await completePages((offset) => {
      let query = db.from("task_proposals").select(PROPOSAL_COLUMNS, { count: "exact" })
        .eq("workspace_id", workspaceId).eq("created_issue_id", issueId).eq("status", "applied")
        .order("id", { ascending: true }).range(offset, offset + PERSONAL_FOCUS_SOURCE_PAGE_SIZE - 1);
      if (signal) query = query.abortSignal(signal);
      return query;
    }, check);
    const batchIds = new Set<string>();
    for (const row of proposals) {
      if (row.workspace_id !== workspaceId || row.created_issue_id !== issueId || !isId(row.batch_id)) fail("invalid_response");
      batchIds.add(row.batch_id);
    }
    const batches = batchIds.size ? await completePages((offset) => {
      let query = db.from("task_proposal_batches").select(BATCH_COLUMNS, { count: "exact" })
        .eq("workspace_id", workspaceId).in("id", [...batchIds])
        .order("id", { ascending: true }).range(offset, offset + PERSONAL_FOCUS_SOURCE_PAGE_SIZE - 1);
      if (signal) query = query.abortSignal(signal);
      return query;
    }, check) : [];
    const foundBatches = new Set<string>();
    const relations = new Map<string, AuthorizedFocusSourceRelation>();
    for (const row of batches) {
      if (row.workspace_id !== workspaceId || !isId(row.id) || !batchIds.has(row.id) || foundBatches.has(row.id) ||
        (row.source_type !== "doc" && row.source_type !== "note") || !isId(row.source_id)) fail("invalid_response");
      foundBatches.add(row.id);
      relations.set(`${row.source_type}:${row.source_id}`, { workspaceId, issueId, sourceType: row.source_type, sourceId: row.source_id });
    }
    // A source batch hidden or removed between reads is not a partial success.
    if (foundBatches.size !== batchIds.size) fail("unavailable");
    await verifyIdentity();
    return [...relations.values()];
  } catch (error) {
    check();
    if (error instanceof PersonalFocusDataError) throw error;
    return fail("query_failed");
  } finally {
    try { subscription?.unsubscribe(); } catch { /* Cleanup must not expose provider text. */ }
  }
}
